import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  Bell, CalendarDays, Check, Copy, CreditCard, Gift, Globe, HelpCircle, LogOut, Mail, Settings, ShoppingBag, Smartphone, Sparkles,
} from "lucide-react";
import {
  ANNUAL, Avatar, Btn, Card, Chip, DENVER, Hand, JoinButton, List, ME, P, PLANS, PLink, PortalShell, Row, Section, Segmented, Sheet, TierLock,
  can, fmtDay, nextClubSession, planFor, timeLabel, upcomingSessions, usePortal,
  type Thing,
} from "./Portal";
import { useToast } from "./Join";
import "./Me.css";

/* /blue-2/portal/me — the member's hub (research §5 "Settings, billing, tier,
   gift, notifications, calendar feed → Me").

   Profile, plan & billing (paid / annual / comp variants), notifications per
   type with email + push (class reminders the day before and 15 minutes
   before), calendar feed, time zone, gift, shop, settings and sign out.
   Visitors get a sign-in screen. Everything is local state; billing and
   sign-in only show a toast. */

/* ─────────── Time zone (prototype: applies app-wide) ───────────
   The shell reads ME.tz (via viewerTz) for every "your time". Until the shell
   owns a setting, the choice is kept in localStorage and applied to ME here at
   load, so every screen picks it up on its next render. */
const TZ_KEY = "pt-tz";
export const ZONES: { tz: string; label: string; abbr: string }[] = [
  { tz: "America/New_York", label: "Eastern", abbr: "ET" },
  { tz: "America/Chicago", label: "Central", abbr: "CT" },
  { tz: DENVER, label: "Mountain (Chelsea's time)", abbr: "MT" },
  { tz: "America/Phoenix", label: "Arizona", abbr: "MST" },
  { tz: "America/Los_Angeles", label: "Pacific", abbr: "PT" },
  { tz: "America/Anchorage", label: "Alaska", abbr: "AKT" },
  { tz: "Pacific/Honolulu", label: "Hawaii", abbr: "HT" },
  { tz: "America/Halifax", label: "Atlantic (Halifax)", abbr: "AT" },
  { tz: "Europe/London", label: "UK (London)", abbr: "UK" },
  { tz: "Australia/Sydney", label: "Australia (Sydney)", abbr: "AET" },
];
function applyTz(tz: string) {
  const z = ZONES.find(x => x.tz === tz);
  if (!z) return;
  ME.tz = z.tz; ME.tzAbbr = z.abbr;
}
try { const saved = localStorage.getItem(TZ_KEY); if (saved) applyTz(saved); } catch { /* storage blocked */ }

/* ─────────── Notifications ─────────── */

const STUDIO: { group: string; items: Pref[] } = { group: "Studio", items: [
  { id: "newq", label: "New questions in #help", note: "Answer from the thread", need: "manage", email: true, push: true },
  { id: "reports", label: "Reported messages", need: "manage", email: true, push: true },
  { id: "members", label: "New members and cancellations", note: "Daily summary", need: "manage", email: true, push: false },
  { id: "processed", label: "Replay finished processing", note: "Ready to add chapters", need: "manage", email: false, push: true },
] };

type Pref = { id: string; label: string; note?: string; need: Thing; email: boolean; push: boolean; lockEmail?: boolean };
const PREFS: { group: string; items: Pref[] }[] = [
  { group: "Class reminders", items: [
    { id: "dayBefore", label: "The day before", note: "With the supply checklist", need: "live", email: ME.notify.dayBefore, push: false },
    { id: "fifteen", label: "15 minutes before", need: "live", email: false, push: ME.notify.fifteenMin },
    { id: "golive", label: "Chelsea is live", need: "live", email: false, push: true },
    { id: "replay", label: "Replay is ready", note: "With chapters and fix notes", need: "replays", email: true, push: true },
  ] },
  { group: "From Chelsea", items: [
    { id: "changes", label: "Reschedules and cancellations", note: "Always emailed, so you never miss one", need: "notes", email: true, push: true, lockEmail: true },
    { id: "notes", label: "Notes", need: "notes", email: false, push: true },
    { id: "project", label: "Next month's project", note: "Supply list and pattern code", need: "notes", email: true, push: false },
    { id: "youtube", label: "Free YouTube lives", need: "notes", email: false, push: true },
  ] },
  { group: "Community", items: [
    { id: "answers", label: "Answers to my questions", need: "qaAsk", email: true, push: true },
    { id: "replies", label: "Replies and mentions", need: "communityPost", email: false, push: true },
    { id: "digest", label: "Weekly makes digest", need: "communityRead", email: true, push: false },
  ] },
];

function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
    className={`ptm-sw${on ? " is-on" : ""}`} onClick={() => onChange(!on)}><i aria-hidden="true" /></button>;
}

/* ─────────── Screen ─────────── */

type Billing = "monthly" | "annual" | "comp" | "gift";

export function PortalMe() {
  const { role } = usePortal();
  const [toast, show] = useToast();
  if (role === "visitor") return <SignIn toast={toast} show={show} />;
  return <Hub toast={toast} show={show} />;
}

function Hub({ toast, show }: { toast: ReactNode; show: (t: ReactNode) => void }) {
  const { role, now, setRole } = usePortal();
  const isChelsea = role === "chelsea";
  const name = isChelsea ? "Chelsea" : ME.name;
  const [giftOpen, setGiftOpen] = useState(false);
  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (h) window.setTimeout(() => document.getElementById(h)?.scrollIntoView({ block: "center" }), 80);
  }, []);

  const rail = <>
    <CalendarFeed show={show} />
    <GiftCard onOpen={() => setGiftOpen(true)} />
    <List label="More">
      <Row lead={<span className="ptm-ico"><ShoppingBag size={18} /></span>} title="Shop" meta="Patterns, kits and notions" to="/blue-2/shop" />
      <Row lead={<span className="ptm-ico"><Settings size={18} /></span>} title="Account settings" meta="Email, password, privacy, delete account" to="/blue-2/members/account" />
      <Row lead={<span className="ptm-ico"><HelpCircle size={18} /></span>} title="Help and FAQ" meta="Plans, trial, replays" to={`${P.join}#faq`} />
      <Row lead={<span className="ptm-ico"><Sparkles size={18} /></span>} title="Moving from Patreon" meta="Carry over your badge and history" to={`${P.join}#patreon`} />
      <Row lead={<span className="ptm-ico is-out"><LogOut size={18} /></span>} title="Sign out" onClick={() => { setRole("visitor"); show("Signed out. Prototype: you're now seeing the visitor view."); }} chevron={false} />
    </List>
  </>;

  return <PortalShell active="me" title="Me" subtitle={isChelsea ? "Your creator account" : "Your membership, reminders and settings"} rail={rail}>
    {toast}
    <Profile name={name} />
    <PlanCard show={show} />
    <TimeZone now={now} show={show} />
    <Notifications show={show} />
    <Sheet open={giftOpen} onClose={() => setGiftOpen(false)} title="Gift a membership"
      footer={<GiftFooter onDone={() => { setGiftOpen(false); show("Prototype: this would open checkout for the gift. Nothing is charged here."); }} />}>
      <GiftForm />
    </Sheet>
  </PortalShell>;
}

/* ─────────── Profile ─────────── */

function Profile({ name }: { name: string }) {
  const { role } = usePortal();
  const isChelsea = role === "chelsea";
  return <Card pad="lg" className="ptm-profile" tape="green" tapeSide="left">
    <Avatar person={isChelsea ? "chelsea" : ME} size={72} />
    <div className="ptm-profile-main">
      <p className="ptm-profile-name">{name}
        {isChelsea ? <span className="pt-badge pt-badge-chelsea">Chelsea</span> : ME.founding && role === "member" && <span className="pt-badge">Founding</span>}
      </p>
      <p className="ptm-profile-meta">{isChelsea ? "Utah · club host since 2019" : `${ME.city} · ${role === "member" ? "member since" : "joined"} ${ME.memberSince}`}</p>
      <p className="ptm-profile-meta">{isChelsea ? "chelsea@example.com" : ME.email}</p>
      {role === "member" && ME.founding && <Hand className="ptm-profile-hand">founding member ♥</Hand>}
    </div>
    <Btn variant="secondary" size="sm" to="/blue-2/members/account" className="ptm-profile-edit">Edit profile</Btn>
  </Card>;
}

/* ─────────── Plan & billing ─────────── */

function PlanCard({ show }: { show: (t: ReactNode) => void }) {
  const { role } = usePortal();
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("billing");
  const billing: Billing = raw === "annual" || raw === "comp" || raw === "gift" ? raw : "monthly";
  const setBilling = (b: Billing) => setSp(prev => { const n = new URLSearchParams(prev); if (b === "monthly") n.delete("billing"); else n.set("billing", b); return n; }, { replace: true, preventScrollReset: true });
  const next = fmtDay(ME.nextBilling, DENVER, { month: "short", day: "numeric", year: "numeric" });
  const manage = () => show("Prototype: this opens billing (Stripe customer portal): card, invoices, switch plan, cancel.");

  if (role === "chelsea") return <Section title="Plan" id="plan">
    <Card pad="md" className="ptm-plan">
      <div className="ptm-plan-top"><div><p className="pt-kicker">Creator account</p><p className="ptm-plan-name">You run the club</p></div><Chip tone="peri">Creator</Chip></div>
      <p className="ptm-plan-line">Members, payouts and the studio live in Studio (not in this prototype).</p>
      <div className="ptm-plan-btns"><Btn variant="primary" onClick={() => show("Prototype: Studio isn't part of this prototype.")}>Open Studio</Btn></div>
    </Card>
  </Section>;

  if (role === "free") return <Section title="Plan" id="plan">
    <Card pad="md" className="ptm-plan">
      <div className="ptm-plan-top"><div><p className="pt-kicker">Your plan</p><p className="ptm-plan-name">Free account</p></div><Chip>Free</Chip></div>
      <p className="ptm-plan-line">You get notes from Chelsea, supply lists, the free YouTube lives and the community to read.</p>
      <div className="ptm-upsell">
        <p><b>Sew along live.</b> Members get 2 classes a month, every replay with chapters, and the pattern codes.</p>
        <div className="ptm-plan-btns"><JoinButton need="member" /><JoinButton need="friend" variant="ghost" /></div>
      </div>
    </Card>
  </Section>;

  if (role === "friend") return <Section title="Plan" id="plan">
    <Card pad="md" className="ptm-plan">
      <div className="ptm-plan-top"><div><p className="pt-kicker">Your plan</p><p className="ptm-plan-name">{PLANS.friend.name} · {PLANS.friend.per}</p></div><Chip tone="blush">Thread Friend</Chip></div>
      <dl className="ptm-dl">
        <div><dt>Paid through</dt><dd>{next}</dd></div>
        <div><dt>Next charge</dt><dd>$3 on {next}</dd></div>
        <div><dt>Card</dt><dd>Visa ending 4242</dd></div>
      </dl>
      <div className="ptm-upsell">
        <p><b>Add live classes for $22 more a month.</b> Your Thread Friend perks stay; you pay the difference for this month.</p>
        <div className="ptm-plan-btns"><JoinButton need="member" /></div>
      </div>
      <div className="ptm-plan-btns"><Btn variant="secondary" icon={<CreditCard size={17} aria-hidden="true" />} onClick={manage}>Manage billing</Btn></div>
    </Card>
  </Section>;

  // Member: billing variants (prototype toggle)
  const comp = billing === "comp" || billing === "gift";
  return <Section title="Plan" id="plan">
    <Card pad="md" className="ptm-plan">
      <div className="ptm-plan-top">
        <div><p className="pt-kicker">Your plan</p>
          <p className="ptm-plan-name">{PLANS.member.name} · {comp ? "free access" : billing === "annual" ? `$${ANNUAL.member}/yr` : PLANS.member.per}</p></div>
        <Chip tone={comp ? "gold" : "teal"}>{billing === "gift" ? "Gift" : billing === "comp" ? "Comp" : "Active"}</Chip>
      </div>
      {billing === "comp" ? <>
        <p className="ptm-plan-line">Chelsea gave you complimentary access. Nothing to pay and no card on file.</p>
        <dl className="ptm-dl">
          <div><dt>Free access</dt><dd>No end date</dd></div>
          <div><dt>Billing</dt><dd>None</dd></div>
        </dl>
        <div className="ptm-plan-btns">
          <Btn variant="secondary" icon={<CreditCard size={17} aria-hidden="true" />} onClick={() => show("Prototype: this would open billing to start paying (some comped members like to support the club).")}>Support with a paid plan</Btn>
        </div>
      </> : billing === "gift" ? <>
        <p className="ptm-plan-line">A gift from <b>Maggie L.</b>: 12 months of the club. Nothing to pay and no card on file.</p>
        <dl className="ptm-dl">
          <div><dt>Free access through</dt><dd>Feb 3, 2027</dd></div>
          <div><dt>After that</dt><dd>Ends, unless you add a card</dd></div>
        </dl>
        <div className="ptm-plan-btns">
          <Btn variant="primary" icon={<CreditCard size={17} aria-hidden="true" />} onClick={() => show("Prototype: this would open billing to add a card for after Feb 3, 2027.")}>Add a card for later</Btn>
          <Btn variant="ghost" onClick={() => show("Prototype: this would open a thank-you note to Maggie L.")}>Say thanks to Maggie</Btn>
        </div>
      </> : <>
        <dl className="ptm-dl">
          <div><dt>Paid through</dt><dd>{billing === "annual" ? "Feb 3, 2027" : next}</dd></div>
          <div><dt>Next charge</dt><dd>{billing === "annual" ? `$${ANNUAL.member} on Feb 3, 2027` : `$25 on ${next}`}</dd></div>
          <div><dt>Card</dt><dd>Visa ending 4242</dd></div>
        </dl>
        {billing === "monthly" && <p className="ptm-annual">
          <Sparkles size={16} aria-hidden="true" /><span>Switch to annual and pay <b>${ANNUAL.member}/yr</b>. That's 2 months free.</span>
          <button type="button" onClick={() => show("Prototype: this would switch you to annual, credited for the rest of this month.")}>Switch</button>
        </p>}
        <div className="ptm-plan-btns">
          <Btn variant="secondary" icon={<CreditCard size={17} aria-hidden="true" />} onClick={manage}>Manage billing</Btn>
          <Btn variant="ghost" onClick={() => show("Prototype: billing would offer a pause, a discount, or cancel at the end of the paid period.")}>Pause or cancel</Btn>
        </div>
      </>}
      <div className="ptm-proto" aria-label="Prototype variant">
        <span>Preview</span>
        <Segmented size="sm" label="Billing variant" value={billing} onChange={setBilling}
          options={[{ value: "monthly", label: "Monthly" }, { value: "annual", label: "Annual" }, { value: "comp", label: "Comp" }, { value: "gift", label: "Gift" }]} />
      </div>
    </Card>
  </Section>;
}

/* ─────────── Notifications ─────────── */

function Notifications({ show }: { show: (t: ReactNode) => void }) {
  const { role } = usePortal();
  const [prefs, setPrefs] = useState<Record<string, { email: boolean; push: boolean }>>(() =>
    Object.fromEntries([STUDIO, ...PREFS].flatMap(g => g.items).map(p => [p.id, { email: p.email, push: p.push }])));
  const [pushOn, setPushOn] = useState(ME.notify.push);
  const groups = role === "chelsea" ? [STUDIO, ...PREFS.slice(1)] : PREFS;
  const set = (id: string, k: "email" | "push", v: boolean) => setPrefs(p => ({ ...p, [id]: { ...p[id], [k]: v } }));

  return <Section title="Notifications" id="notifications"
    action={<Btn variant="ghost" size="sm" icon={<Bell size={16} aria-hidden="true" />} onClick={() => show("Test notification: “See you at 7!! Have your quilted panels next to the machine.”")}>Send a test</Btn>}>
    <div className="ptm-push">
      <Smartphone size={18} aria-hidden="true" />
      <p><b>Push on this device</b><span>{pushOn ? "On. Reminders arrive even when the club is closed." : "Off. You'll only get emails."}</span></p>
      <Switch on={pushOn} onChange={setPushOn} label="Push notifications on this device" />
    </div>
    <div className="ptm-notif" role="table" aria-label="Notification settings">
      <div className="ptm-notif-head" role="row">
        <span role="columnheader">What</span>
        <span role="columnheader"><Mail size={14} aria-hidden="true" />Email</span>
        <span role="columnheader"><Smartphone size={14} aria-hidden="true" />Push</span>
      </div>
      {groups.map(g => {
        const locked = g.items.filter(p => !can(role, p.need));
        // A whole group behind one tier gets one lock, not one per row.
        const allLocked = locked.length === g.items.length && new Set(locked.map(p => planFor(p.need))).size === 1;
        return <div key={g.group} role="rowgroup">
        <p className="ptm-notif-group" role="row"><span role="cell">{g.group}</span></p>
        {allLocked ? <div className="ptm-notif-row is-locked is-group" role="row">
          <span className="ptm-notif-label" role="rowheader">{g.items.map(p => p.label).join(" · ")}
            <TierLock thing={g.items[0].need} variant="inline" /></span>
        </div> : g.items.map(p => {
          const ok = can(role, p.need);
          const v = prefs[p.id];
          return <div key={p.id} className={`ptm-notif-row${ok ? "" : " is-locked"}`} role="row">
            <span className="ptm-notif-label" role="rowheader">{p.label}{p.note && <small>{p.note}</small>}
              {!ok && <TierLock thing={p.need} variant="inline" />}</span>
            {ok ? <>
              <span role="cell"><Switch on={v.email || !!p.lockEmail} disabled={p.lockEmail} onChange={x => set(p.id, "email", x)} label={`${g.group}: ${p.label} by email`} /></span>
              <span role="cell"><Switch on={pushOn && v.push} disabled={!pushOn} onChange={x => set(p.id, "push", x)} label={`${g.group}: ${p.label} by push`} /></span>
            </> : <><span role="cell" /><span role="cell" /></>}
          </div>;
        })}
      </div>;
      })}
    </div>
  </Section>;
}

/* ─────────── Time zone ─────────── */

function TimeZone({ now, show }: { now: Date; show: (t: ReactNode) => void }) {
  const { role } = usePortal();
  const [tz, setTz] = useState(role === "chelsea" ? DENVER : ME.tz);
  const next = nextClubSession(now) ?? upcomingSessions(now, 1)[0];
  const z = ZONES.find(x => x.tz === tz);
  const change = (v: string) => {
    setTz(v); applyTz(v);
    try { localStorage.setItem(TZ_KEY, v); } catch { /* storage blocked */ }
    show(`Times now show in ${ZONES.find(x => x.tz === v)?.label ?? v} next to Mountain time, everywhere in the club.`);
  };
  return <Section title="Time zone" id="timezone">
    <Card pad="md" className="ptm-tz">
      <div className="ptm-tz-row">
        <Globe size={20} aria-hidden="true" className="ptm-tz-ico" />
        <label className="ptm-tz-label" htmlFor="ptm-tz-sel">Show class times in</label>
        <select id="ptm-tz-sel" className="ptm-select" value={tz} onChange={e => change(e.target.value)} disabled={role === "chelsea"}>
          {ZONES.map(x => <option key={x.tz} value={x.tz}>{x.label}</option>)}
        </select>
      </div>
      {next && <p className="ptm-tz-preview">
        <span className="pt-kicker">Preview</span>
        <span><b>{next.title}</b> · {fmtDay(next.start, tz)} · {timeLabel(next.start, tz, z?.abbr ?? "your time")}</span>
      </p>}
      <p className="ptm-tz-note">{role === "chelsea" ? "You see Mountain time only." : "This device says Eastern. Mountain time is always shown too, since that's Chelsea's time."}</p>
    </Card>
  </Section>;
}

/* ─────────── Calendar feed ─────────── */

function CalendarFeed({ show }: { show: (t: ReactNode) => void }) {
  const { role } = usePortal();
  const [withYt, setWithYt] = useState(true);
  const [copied, setCopied] = useState(false);
  const url = `webcal://loosethreads.club/cal/${role === "chelsea" ? "studio-c9x1m4" : "rosie-h-7f3k2q"}${withYt ? "" : "?club=only"}.ics`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* prototype */ }
    setCopied(true); window.setTimeout(() => setCopied(false), 1800);
  };
  if (!can(role, "calendarFeed")) return <Card pad="md" className="ptm-cal">
    <p className="ptm-cal-t"><CalendarDays size={18} aria-hidden="true" />Calendar feed</p>
    <TierLock thing="calendarFeed" variant="inline" />
    <p className="ptm-cal-sub">Every class lands in your calendar and moves when a class is rescheduled. Free lives can still be added one at a time from the schedule.</p>
  </Card>;
  return <Card pad="md" className="ptm-cal" id="calendar">
    <p className="ptm-cal-t"><CalendarDays size={18} aria-hidden="true" />Calendar feed</p>
    <p className="ptm-cal-sub">Subscribe once. Every class shows up in your calendar with a 15-minute alarm, and reschedules update by themselves.</p>
    <button type="button" className={`ptm-feed${copied ? " is-copied" : ""}`} onClick={copy} aria-label="Copy calendar feed link">
      <span className="ptm-feed-url">{url}</span>
      <span className="ptm-feed-act" aria-live="polite">{copied ? <><Check size={15} aria-hidden="true" />Copied</> : <><Copy size={15} aria-hidden="true" />Copy</>}</span>
    </button>
    <label className="ptm-check"><input type="checkbox" checked={withYt} onChange={e => setWithYt(e.target.checked)} />Include free YouTube lives</label>
    <div className="ptm-cal-btns">
      <Btn size="sm" variant="secondary" onClick={() => show("Prototype: this would open Apple Calendar to subscribe.")}>Apple Calendar</Btn>
      <Btn size="sm" variant="secondary" onClick={() => show("Prototype: this would open Google Calendar → Add by URL.")}>Google</Btn>
    </div>
  </Card>;
}

/* ─────────── Gift ─────────── */

const GIFTS = [{ m: 1, price: 25 }, { m: 3, price: 75 }, { m: 12, price: ANNUAL.member }];

function GiftCard({ onOpen }: { onOpen: () => void }) {
  return <Card tone="gold" pad="md" className="ptm-gift" id="gift">
    <p className="ptm-gift-t"><Gift size={18} aria-hidden="true" />Gift a membership</p>
    <p className="ptm-gift-sub">1, 3 or 12 months of live classes and replays. You pick the day it arrives.</p>
    <Btn variant="primary" size="sm" onClick={onOpen}>Choose a gift</Btn>
  </Card>;
}

function GiftForm() {
  const [m, setM] = useState(3);
  return <div className="ptm-giftform">
    <fieldset>
      <legend>Length</legend>
      <div className="ptm-giftopts">
        {GIFTS.map(g => <label key={g.m} className={`ptm-giftopt${m === g.m ? " is-on" : ""}`}>
          <input type="radio" name="gift-len" checked={m === g.m} onChange={() => setM(g.m)} />
          <b>{g.m === 12 ? "1 year" : `${g.m} month${g.m > 1 ? "s" : ""}`}</b><span>${g.price}</span>
        </label>)}
      </div>
    </fieldset>
    <label className="ptm-field"><span>Their name</span><input type="text" placeholder="e.g. Aunt June" /></label>
    <label className="ptm-field"><span>Their email</span><input type="email" placeholder="name@example.com" /></label>
    <label className="ptm-field"><span>Send on</span><input type="date" defaultValue="2026-12-24" /></label>
    <label className="ptm-field"><span>A note (optional)</span><textarea rows={3} placeholder="Can't wait to sew with you!" /></label>
    <p className="ptm-giftnote">Gifts don't renew. They'll get an email on the day with a link to join.</p>
  </div>;
}
function GiftFooter({ onDone }: { onDone: () => void }) {
  return <Btn block size="lg" onClick={onDone}>Continue to checkout</Btn>;
}

/* ─────────── Visitor: sign in ─────────── */

function SignIn({ toast, show }: { toast: ReactNode; show: (t: ReactNode) => void }) {
  const { setRole } = usePortal();
  const [email, setEmail] = useState("");
  return <PortalShell active="me" title="Sign in" subtitle="No password needed. We'll email you a link.">
    {toast}
    <div className="ptm-signin">
      <Card pad="lg" className="ptm-signin-card" tape="yellow">
        <form onSubmit={e => { e.preventDefault(); show(`Prototype: we'd email a sign-in link to ${email || "you"}. Signing you in as the sample member.`); setRole("member"); }}>
          <label className="ptm-field"><span>Email</span>
            <input type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} /></label>
          <Btn type="submit" block size="lg" icon={<Mail size={18} aria-hidden="true" />}>Email me a sign-in link</Btn>
        </form>
        <p className="ptm-signin-note">Coming from Patreon? Use the email on your Patreon account and your history comes with you. <PLink to={`${P.join}#patreon`}>How it works</PLink></p>
      </Card>
      <Card tone="paper" pad="lg" className="ptm-signin-new">
        <Hand>new here?</Hand>
        <p className="ptm-signin-t">Sew along with Chelsea</p>
        <ul>
          <li><Check size={16} aria-hidden="true" />Free account: notes, supply lists, the community</li>
          <li><Check size={16} aria-hidden="true" />Thread Friend ($3): studio posts, share your makes</li>
          <li><Check size={16} aria-hidden="true" />Member ($25): live classes, every replay, pattern codes</li>
        </ul>
        <div className="ptm-plan-btns">
          <Btn to={`${P.join}?plan=member`}>Compare plans</Btn>
          <Btn variant="ghost" to={`${P.join}?plan=free`}>Create a free account</Btn>
        </div>
      </Card>
    </div>
  </PortalShell>;
}
